<script setup>
import { useI18n } from 'vue-i18n';
import { computed, onMounted, ref } from 'vue';
import _ from '@/lib/lodash';

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default() {
      return '';
    },
  },
  label: {
    type: String,
    default() {
      return '';
    },
  },
  placeholder: {
    type: [String, Number],
    default() {
      return '';
    },
  },
  prependIcon: {
    type: String,
    default() {
      return '';
    },
  },
  disabled: {
    type: Boolean,
    default() {
      return false;
    },
  },
  isSearch: {
    type: Boolean,
    default() {
      return false;
    },
  },
  isClearable: {
    type: Boolean,
    default() {
      return false;
    },
  },
  required: {
    type: Boolean,
    default() {
      return false;
    },
  },
  isError: {
    type: Boolean,
    default() {
      return false;
    },
  },
  error: {
    type: [String, Number, Array],
    default() {
      return '';
    },
  },
  errorClearOnChange: {
    type: Boolean,
    default: true,
  },
  autofocus: {
    type: Boolean,
    default: false,
  },
  type: {
    type: String,
    default() {
      return 'text';
    },
  },
  wrapperType: {
    type: String,
    default() {
      return 'default';
    },
  },
  name: {
    type: String,
  },
  tabindex: {
    type: Number,
    default() {
      return 1;
    },
  },
  help: {
    type: String,
    default() {
      return '';
    },
  },
});

const emit = defineEmits(['update:modelValue', 'update:error', 'change', 'clear', 'search']);

const { t } = useI18n();

//#region data
const focused = ref(false);
const passwordIsShow = ref(false);
const uiInput = ref(null);
//#endregion

//#region computed
const previousIcon = computed(() => {
  return props.prependIcon ? props.prependIcon : props.isSearch ? 'search' : '';
});
const currentError = computed(() => {
  return _.isArray(props.error) ? props.error[0].message : props.error;
});
const pPlaceholder = computed(() => {
  return props.placeholder !== '' ? props.placeholder : props.isSearch ? t('promoCodes.promocodes.search') : '';
});
const currentType = computed(() => {
  return props.type !== 'password' || (props.type === 'password' && !passwordIsShow.value) ? props.type : 'text';
});
const showClear = computed(() => {
  return props.isSearch && !props.disabled && props.modelValue !== '';
});
//#endregion


onMounted(() => {
  if (props.autofocus) {
    uiInput.value.focus();
  }

});

//#region methods
const handleInput = (event) => {
  const { value } = event.target;
  emit('update:modelValue', value, event);

  if (props.errorClearOnChange) {
    emit('update:error', null);
  }
};
const handleFocus = () => {
  focused.value = true;
};
const handleBlur = () => {
  focused.value = false;
};
const handleEnter = () => {
  focused.value = false;
  emit('search', props.modelValue);
};
const showPassword = () => {
  passwordIsShow.value = !passwordIsShow.value;
};
const clear = () => {
  if (props.modelValue !== '') {
    emit('update:modelValue', '');
    emit('change', '');
    emit('clear');
    emit('search');
  }
};
//#endregion


</script>

<template>
  <label
    :class="[
      'ui-input',
      `ui-input_${wrapperType}`,
      { 'ui-input_focus': focused },
      { 'ui-input_error': currentError || isError },
      { 'ui-input_disabled': disabled },
    ]"
  >
    <UiLabel
      v-if="label"
      :label="label"
      :required="required"
      :type="wrapperType"
    />
    <span
      :class="[
        'ui-input__wrapper',
        { 'ui-input__wrapper_previous-icon': previousIcon }

      ]"
    >
      <UiIcon
        v-if="previousIcon !== ''"
        :name="previousIcon"
        :size="12"
        class="ui-input__pre-icon"
        color="#a1a1a1"
        lib="fa"
        substyle="fas"
      />
      <input
        ref="uiInput"
        :disabled="disabled"
        :name="name"
        :placeholder="pPlaceholder"
        :tabindex="tabindex"
        :type="currentType"
        :value="modelValue"
        autocomplete="false"
        class="ui-input__element"
        @blur="handleBlur"
        @focus="handleFocus"
        @input="handleInput"
        @keyup.enter="handleEnter"
      >
      <slot name="nextIcon">
        <span
          v-if="isClearable || isSearch"
          :class="[
            'ui-input__clear',
            { 'ui-input__clear_show': showClear }
          ]"
          @click="clear"
        >
          <UiIcon
            :size="12"
            color="#666"
            lib="fa"
            name="xmark"
            substyle="fas"
          />
        </span>
      </slot>
      <span
        v-if="type === 'password'"
        class="ui-input__show-password"
        @click="showPassword"
      >
        <UiIcon
          :name="passwordIsShow ? 'eye-slash' : 'eye'"
          :size="14"
          color="#a1a1a1"
          lib="fa"
          substyle="fas"
        />
      </span>


    </span>
    <UiHelp
      v-if="help !== ''"
      :text="help"
      class="ui-input__help"
    />
    <UiHelp
      v-if="currentError"
      :is-error="true"
      :text="currentError"
      class="ui-input__help"
    />

  </label>
</template>

<style lang="scss">
.ui-input {
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  /* Chrome, Safari, Edge, Opera */
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  input[type='number'] {
    -moz-appearance: textfield;
  }

  &__wrapper {
    display: inline-flex;
    align-items: center;
    background-color: #ffffff;
    transition: border-color 0.2s;
    overflow: hidden;
    box-sizing: border-box;

    input:-webkit-autofill,
    input:-webkit-autofill:hover,
    input:-webkit-autofill:focus,
    input:-webkit-autofill:active {
      -webkit-box-shadow: 0 0 0 30px #ffffff inset !important;
      color: #ffffff !important;
    }

    &_previous-icon {
      padding-left: 8px;
      input.ui-input__element {
        padding-left: 8px;
      }
    }
  }

  &__element {
    font-family: "Open Sans", sans-serif;
    display: flex;
    align-items: center;
    min-width: 10%;
    max-width: 100%;
    flex: 10 1 auto;
    border: none;
    outline: none;
    background: transparent;
    font-size: 12px;
    color: #000;
  }

  &__clear {
    transition: all 0.2s;
    line-height: 12px;
    opacity: 0;
    margin-right: 8px;

    &_show {
      opacity: 1;
      cursor: pointer;
    }
  }

  &__show-password {
    cursor: pointer;
    width: 18px;
    height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &_default & {
    &__wrapper {
      border: 1px solid #d9d9d9;
      border-radius: 8px;
      height: 34px;
    }

    &__element {
      padding: 8px 16px;
    }

    &__show-password {
      margin-right: 12px;
    }
  }

  &_registration & {
    &__wrapper {
      border-bottom: 2px solid #ccc;
      border-radius: 0;
      padding: 0;
      height: 45px;
    }

    &__element {
      font-size: 14px;
      padding: 11px 11px 11px 1px;
    }
  }


  &_error & {
    &__wrapper {
      border-color: #ff5c70;
    }
  }

  &_focus & {
    &__wrapper {
      border-color: var(--color-border);
    }
  }

  &_disabled {
    opacity: 0.5;
  }
}
</style>